/**
 * referral-queue-table.tsx — Referral queue list table component.
 *
 * Displays referrals with columns: Patient Name, Submission Date,
 * Provider Name, NPI, Source, Status. Clicking a row navigates to the
 * detail page.
 *
 * Props:
 *   items     — array of PendingReferralSummary (already filtered)
 *   isLoading — show Spinner via Table.ScrollArea when true
 */

import { Table } from '@nrx/ui';
import type { JSX } from 'react';
import { useNavigate } from 'react-router';

import type { PendingReferralSummary, ReferralQueueStatus } from './referral-api.js';

export interface ReferralQueueTableProps {
  items: PendingReferralSummary[];
  isLoading: boolean;
  activeStatus?: ReferralQueueStatus;
}

const EMPTY_STATE_TEXT: Record<ReferralQueueStatus, string> = {
  awaiting_provider_review: 'No referrals awaiting review.',
  rejected: 'No rejected referrals.',
  ineligible: 'No ineligible referrals.',
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: PendingReferralSummary['status'] }): JSX.Element {
  const colours: Record<PendingReferralSummary['status'], string> = {
    awaiting_provider_review: 'bg-yellow-100 text-yellow-800',
    eligibility_pending: 'bg-blue-100 text-blue-800',
    ineligible: 'bg-red-100 text-red-800',
    rejected: 'bg-gray-100 text-gray-700',
  };
  const labels: Record<PendingReferralSummary['status'], string> = {
    awaiting_provider_review: 'Awaiting Review',
    eligibility_pending: 'Eligibility Pending',
    ineligible: 'Ineligible',
    rejected: 'Rejected',
  };
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${colours[status]}`}
    >
      {labels[status]}
    </span>
  );
}

export function ReferralQueueTable({
  items,
  isLoading,
  activeStatus,
}: ReferralQueueTableProps): JSX.Element {
  const navigate = useNavigate();

  if (!isLoading && items.length === 0) {
    const emptyText =
      activeStatus != null ? EMPTY_STATE_TEXT[activeStatus] : 'No referrals awaiting review.';
    return <p className="py-8 text-center text-gray-500">{emptyText}</p>;
  }

  return (
    <div className="overflow-x-auto rounded border border-gray-200">
      <Table.ScrollArea loading={isLoading} loadingLabel="Loading referrals">
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Patient Name</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Submission Date</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Provider Name</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>NPI</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Source</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.map((item) => (
              <Table.Row
                key={item.referralId}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/referral-queue/unknown-provider/${item.referralId}`)}
              >
                <Table.Cell>
                  {item.patientName.firstName} {item.patientName.lastName}
                </Table.Cell>
                <Table.Cell className="text-gray-600">{formatDate(item.submissionDate)}</Table.Cell>
                <Table.Cell>{item.providerName}</Table.Cell>
                <Table.Cell className="font-mono text-gray-600">{item.providerNpi}</Table.Cell>
                <Table.Cell className="capitalize text-gray-600">
                  {item.referralSource.replace(/_/g, ' ')}
                </Table.Cell>
                <Table.Cell>
                  <StatusBadge status={item.status} />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Table.ScrollArea>
    </div>
  );
}
